import { useEffect, useState } from "react";
import { Link, useParams, useNavigate, useLocation } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { useAuth } from "../auth/AuthContext";
import { useSiteConfig } from "../config/SiteConfigContext";
import { apiFetch } from "../api/client";
import { useIsMobile } from "../hooks/useIsMobile";
import { formatEuro } from "../lib/format";

// Termini e Condizioni su Iubenda (stesso account/ID della cookie policy e del footer).
const TERMS_URL = "https://www.iubenda.com/terms-and-conditions/97685173";

interface OrderItem {
  id: string;
  product_id: string | null;
  product_title: string;
  quantity: number;
  price_cents: number;
}

interface Order {
  id: string;
  order_number: number | null;
  created_at: string;
  total_cents: number;            // LORDO (IVA inclusa)
  currency: string;
  status: string;
  // Ripartizione IVA (null sugli ordini pre-IVA)
  subtotal_cents?: number | null;
  discount_cents?: number | null;
  taxable_cents?: number | null;
  vat_rate?: number | null;
  vat_cents?: number | null;
  items: OrderItem[];
}

const STATUS_COLORS: Record<string, { bg: string; color: string }> = {
  PENDING: { bg: "#fef3c7", color: "#92400e" },
  PAID: { bg: "#dcfce7", color: "#166534" },
  SHIPPED: { bg: "#dbeafe", color: "#1e40af" },
  DELIVERED: { bg: "#dcfce7", color: "#14532d" },
  CANCELLED: { bg: "#fecaca", color: "#991b1b" },
};

export function Account() {
  const { locale } = useParams();
  const { t } = useTranslation();
  const { user, logout } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();
  const isMobile = useIsMobile();
  const cfg = useSiteConfig();

  const [orders, setOrders] = useState<Order[]>([]);
  const [loadingOrders, setLoadingOrders] = useState(true);
  const [ordersError, setOrdersError] = useState("");
  const [expanded, setExpanded] = useState<Set<string>>(new Set());
  const [showSuccess, setShowSuccess] = useState(!!(location.state as { orderPlaced?: boolean })?.orderPlaced);

  useEffect(() => {
    apiFetch<Order[]>("/api/v1/shop/orders")
      .then(setOrders)
      .catch(() => setOrdersError(t("account.ordersLoadError")))
      .finally(() => setLoadingOrders(false));
  }, []);

  useEffect(() => {
    if (showSuccess) {
      const timer = setTimeout(() => setShowSuccess(false), 5000);
      return () => clearTimeout(timer);
    }
  }, [showSuccess]);

  const formatPrice = (cents: number) => formatEuro(cents);

  const formatDate = (iso: string) => {
    if (!iso) return "—";
    return new Date(iso).toLocaleDateString(locale === "it" ? "it-IT" : "en-GB", {
      day: "2-digit",
      month: "short",
      year: "numeric",
    });
  };

  const toggleExpand = (orderId: string) => {
    setExpanded((prev) => {
      const next = new Set(prev);
      if (next.has(orderId)) next.delete(orderId);
      else next.add(orderId);
      return next;
    });
  };

  const handleLogout = async () => {
    await logout();
    navigate(`/${locale}`);
  };

  const overline: React.CSSProperties = {
    fontSize: 11,
    fontWeight: 500,
    textTransform: "uppercase",
    letterSpacing: 2,
    color: cfg.color_muted,
    marginBottom: 16,
  };

  if (!user) {
    return (
      <div style={{ padding: "96px 24px", maxWidth: 480, margin: "0 auto", textAlign: "center" }}>
        <svg width="40" height="40" viewBox="0 0 24 24" fill="none" stroke={cfg.color_muted} strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round" style={{ marginBottom: 20 }}>
          <path d="M20 21v-2a4 4 0 0 0-4-4H8a4 4 0 0 0-4 4v2" />
          <circle cx="12" cy="7" r="4" />
        </svg>
        <p style={{ fontSize: 14, color: "rgba(28,75,59,0.86)", marginBottom: 24, lineHeight: 1.6 }}>{t("account.loginRequired")}</p>
        <Link
          to={`/${locale}/login`}
          style={{
            display: "inline-block",
            background: cfg.color_dark,
            color: "#fff",
            padding: "11px 32px",
            borderRadius: 8,
            textDecoration: "none",
            fontWeight: 500,
            fontSize: 13,
            letterSpacing: 0.3,
          }}
        >
          {t("nav.login")}
        </Link>
      </div>
    );
  }

  return (
    <div style={{ padding: isMobile ? "32px 20px 64px" : "64px 24px 96px", maxWidth: 880, margin: "0 auto" }}>
      <div style={overline}>{t("account.title")}</div>
      <h1 style={{
        fontFamily: cfg.font_heading,
        fontSize: isMobile ? 28 : 36,
        fontWeight: 400,
        color: cfg.color_dark,
        margin: "0 0 48px 0",
        lineHeight: 1.2,
      }}>
        {user.email}
      </h1>

      {showSuccess && (
        <div style={{
          background: "#dcfce7",
          color: "#166534",
          padding: "12px 20px",
          borderRadius: 8,
          marginBottom: 32,
          fontSize: 13,
          fontWeight: 500,
          display: "flex",
          justifyContent: "space-between",
          alignItems: "center",
        }}>
          <span>{t("account.orderSuccess")}</span>
          <button
            onClick={() => setShowSuccess(false)}
            style={{ background: "none", border: "none", color: "#166534", fontSize: 16, cursor: "pointer", padding: 0, lineHeight: 1 }}
          >
            &times;
          </button>
        </div>
      )}

      {/* Personal Info */}
      <section style={{ marginBottom: 56 }}>
        <div style={overline}>{t("account.personalInfo")}</div>
        <div style={{
          background: cfg.color_light_bg,
          borderRadius: 8,
          padding: isMobile ? "20px" : "28px 32px",
        }}>
          <div style={{ display: "grid", gridTemplateColumns: isMobile ? "1fr" : "100px 1fr", gap: isMobile ? "4px 0" : "12px 24px" }}>
            <span style={{ fontSize: 12, color: "rgba(28,75,59,0.86)", fontWeight: 500 }}>{t("profile.email")}</span>
            <span style={{ fontSize: 14, fontWeight: 500, color: cfg.color_dark }}>{user.email}</span>
            <span style={{ fontSize: 12, color: "rgba(28,75,59,0.86)", fontWeight: 500 }}>{t("profile.role")}</span>
            <span style={{ fontSize: 14, fontWeight: 500, color: cfg.color_dark }}>{user.role}</span>
            <span style={{ fontSize: 12, color: "rgba(28,75,59,0.86)", fontWeight: 500 }}>{t("profile.id")}</span>
            <span style={{ fontSize: 12, fontFamily: "monospace", color: "rgba(28,75,59,0.86)" }}>{user.id}</span>
          </div>
          <div style={{ display: "flex", flexWrap: "wrap", alignItems: "center", gap: 12, marginTop: 20 }}>
            <button
              onClick={handleLogout}
              style={{
                background: "none",
                border: "1px solid rgba(28,75,59,0.89)",
                color: cfg.color_dark,
                padding: "8px 20px",
                borderRadius: 6,
                fontSize: 12,
                fontWeight: 500,
                cursor: "pointer",
                letterSpacing: 0.3,
                transition: "border-color 0.2s",
              }}
            >
              {t("nav.logout")}
            </button>
            <button
              type="button"
              onClick={() => window.dispatchEvent(new CustomEvent("gb:open-cookie"))}
              title={t("cookie.manage")}
              style={{
                display: "inline-flex",
                alignItems: "center",
                gap: 6,
                background: "none",
                border: "none",
                color: cfg.color_muted,
                padding: "6px 4px",
                fontSize: 12,
                fontWeight: 500,
                cursor: "pointer",
                letterSpacing: 0.3,
                textDecoration: "underline",
              }}
            >
              <svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.7" strokeLinecap="round" strokeLinejoin="round">
                <path d="M12 2a10 10 0 1 0 10 10 4 4 0 0 1-5-5 4 4 0 0 1-5-5Z" />
                <circle cx="9" cy="10" r="0.6" fill="currentColor" />
                <circle cx="14" cy="14" r="0.6" fill="currentColor" />
                <circle cx="10" cy="15" r="0.6" fill="currentColor" />
              </svg>
              {t("cookie.manage")}
            </button>
            <a
              href={TERMS_URL}
              target="_blank"
              rel="noopener noreferrer"
              title={t("footer.terms")}
              style={{
                display: "inline-flex",
                alignItems: "center",
                gap: 6,
                color: cfg.color_muted,
                padding: "6px 4px",
                fontSize: 12,
                fontWeight: 500,
                letterSpacing: 0.3,
                textDecoration: "underline",
              }}
            >
              <svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.7" strokeLinecap="round" strokeLinejoin="round">
                <path d="M14 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V8z" />
                <path d="M14 2v6h6" />
                <path d="M8 13h8M8 17h6" />
              </svg>
              {t("footer.terms")}
            </a>
          </div>
        </div>
      </section>

      {/* Orders */}
      <section style={{ marginBottom: 56 }}>
        <div style={overline}>{t("account.orders")}</div>

        {loadingOrders ? (
          <div style={{ textAlign: "center", padding: 40 }}>
            <div style={{
              width: 20,
              height: 20,
              border: "2px solid rgba(28,75,59,0.89)",
              borderTopColor: cfg.color_primary,
              borderRadius: "50%",
              animation: "spin 0.8s linear infinite",
              margin: "0 auto",
            }} />
          </div>
        ) : ordersError ? (
          <div style={{ background: "#fef2f2", borderRadius: 8, padding: "32px 24px", textAlign: "center" }}>
            <p style={{ fontSize: 13, color: "#991b1b", margin: "0 0 12px 0" }}>{ordersError}</p>
            <button
              onClick={() => {
                setOrdersError("");
                setLoadingOrders(true);
                apiFetch<Order[]>("/api/v1/shop/orders")
                  .then(setOrders)
                  .catch(() => setOrdersError(t("account.ordersLoadError")))
                  .finally(() => setLoadingOrders(false));
              }}
              style={{ background: cfg.color_dark, color: "#fff", border: "none", padding: "8px 20px", borderRadius: 6, cursor: "pointer", fontSize: 12, fontWeight: 500 }}
            >
              {t("common.retry")}
            </button>
          </div>
        ) : orders.length === 0 ? (
          <div style={{ background: cfg.color_light_bg, borderRadius: 8, padding: "40px 24px", textAlign: "center" }}>
            <p style={{ fontSize: 13, color: "rgba(28,75,59,0.86)", margin: 0 }}>{t("account.noOrders")}</p>
          </div>
        ) : (
          <div style={{ display: "flex", flexDirection: "column", gap: 8 }}>
            {orders.map((order) => {
              const statusStyle = STATUS_COLORS[order.status] || { bg: "#f5f5f5", color: "rgba(28,75,59,0.86)" };
              const isExpanded = expanded.has(order.id);
              return (
                <div key={order.id} style={{ borderRadius: 8, border: "1px solid rgba(28,75,59,0.89)", overflow: "hidden" }}>
                  <div
                    onClick={() => toggleExpand(order.id)}
                    style={{
                      display: "flex",
                      justifyContent: "space-between",
                      alignItems: "center",
                      padding: "12px 20px",
                      cursor: "pointer",
                      background: isExpanded ? cfg.color_light_bg : cfg.color_body_bg,
                      transition: "background 0.2s",
                    }}
                  >
                    <div style={{ display: "flex", alignItems: "center", gap: 12, flexWrap: "wrap" }}>
                      <span style={{
                        display: "inline-block",
                        padding: "2px 8px",
                        borderRadius: 4,
                        fontSize: 11,
                        fontWeight: 500,
                        background: statusStyle.bg,
                        color: statusStyle.color,
                      }}>
                        {order.status}
                      </span>
                      {/* Numero ordine progressivo (nuovi) o codice legacy (vecchi ordini) */}
                      <span style={{ fontSize: 13, fontWeight: 600, color: cfg.color_dark }}>
                        #{order.order_number != null ? order.order_number : order.id.slice(0, 8).toUpperCase()}
                      </span>
                      <span style={{ fontSize: 13, color: "rgba(28,75,59,0.86)" }}>{formatDate(order.created_at)}</span>
                    </div>
                    <div style={{ display: "flex", alignItems: "center", gap: 12 }}>
                      <span style={{ fontSize: 14, fontWeight: 500, color: cfg.color_dark }}>
                        {formatPrice(order.total_cents)}
                      </span>
                      <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="#999" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"
                        style={{ transform: isExpanded ? "rotate(180deg)" : "rotate(0)", transition: "transform 0.2s" }}>
                        <polyline points="6 9 12 15 18 9" />
                      </svg>
                    </div>
                  </div>
                  {isExpanded && order.items.length > 0 && (
                    <div style={{ borderTop: "1px solid rgba(28,75,59,0.89)", padding: isMobile ? "12px 12px" : "12px 20px", overflowX: "auto" }}>
                      <table style={{ width: "100%", borderCollapse: "collapse", minWidth: isMobile ? 280 : undefined }}>
                        <thead>
                          <tr>
                            <th style={{ textAlign: "left", fontSize: 11, color: "rgba(28,75,59,0.86)", fontWeight: 500, paddingBottom: 8, textTransform: "uppercase", letterSpacing: "0.05em" }}>
                              {t("account.orderProduct")}
                            </th>
                            <th style={{ textAlign: "right", fontSize: 11, color: "rgba(28,75,59,0.86)", fontWeight: 500, paddingBottom: 8, textTransform: "uppercase", letterSpacing: "0.05em" }}>
                              {t("account.orderQty")}
                            </th>
                            <th style={{ textAlign: "right", fontSize: 11, color: "rgba(28,75,59,0.86)", fontWeight: 500, paddingBottom: 8, textTransform: "uppercase", letterSpacing: "0.05em" }}>
                              {t("account.orderPrice")}
                            </th>
                          </tr>
                        </thead>
                        <tbody>
                          {order.items.map((item) => (
                            <tr key={item.id}>
                              <td style={{ fontSize: 13, padding: "6px 0", color: cfg.color_dark }}>{item.product_title}</td>
                              <td style={{ fontSize: 13, padding: "6px 0", textAlign: "right", color: "rgba(28,75,59,0.86)" }}>{item.quantity}</td>
                              <td style={{ fontSize: 13, padding: "6px 0", textAlign: "right", fontWeight: 500, color: cfg.color_dark }}>
                                {formatPrice(item.price_cents * item.quantity)}
                              </td>
                            </tr>
                          ))}
                        </tbody>
                      </table>
                      {/* Riepilogo economico: imponibile + IVA + totale IVA inclusa (ordini con IVA) */}
                      <div style={{ marginTop: 12, paddingTop: 10, borderTop: "1px solid rgba(28,75,59,0.15)", display: "flex", flexDirection: "column", gap: 4 }}>
                        {order.vat_cents != null && order.vat_rate ? (
                          <>
                            <div style={{ display: "flex", justifyContent: "space-between", fontSize: 12.5, color: "rgba(28,75,59,0.86)" }}>
                              <span>{t("cart.taxableBase")}</span>
                              <span>{formatPrice(order.taxable_cents ?? 0)}</span>
                            </div>
                            <div style={{ display: "flex", justifyContent: "space-between", fontSize: 12.5, color: "rgba(28,75,59,0.86)" }}>
                              <span>{t("cart.vat", { rate: order.vat_rate })}</span>
                              <span>{formatPrice(order.vat_cents)}</span>
                            </div>
                            <div style={{ display: "flex", justifyContent: "space-between", fontSize: 13.5, fontWeight: 600, color: cfg.color_dark }}>
                              <span>{t("cart.totalInclVat")}</span>
                              <span>{formatPrice(order.total_cents)}</span>
                            </div>
                          </>
                        ) : (
                          <div style={{ display: "flex", justifyContent: "space-between", fontSize: 13.5, fontWeight: 600, color: cfg.color_dark }}>
                            <span>{t("cart.total")}</span>
                            <span>{formatPrice(order.total_cents)}</span>
                          </div>
                        )}
                      </div>
                    </div>
                  )}
                </div>
              );
            })}
          </div>
        )}
      </section>

      {/* Invoices */}
      <section>
        <div style={overline}>{t("account.invoices")}</div>
        <div style={{ background: cfg.color_light_bg, borderRadius: 8, padding: "40px 24px", textAlign: "center" }}>
          <p style={{ fontSize: 13, color: "rgba(28,75,59,0.86)", margin: 0 }}>{t("account.invoicesComingSoon")}</p>
        </div>
      </section>
    </div>
  );
}
